import { getLocale } from '@rhino-project/core/i18n';
import { useWatch } from 'react-hook-form';

import { useEffect, useMemo } from 'react';
import { RhinoAttribute } from '@rhino-project/core';
import { dateFilterOperatorLabel, useModelFilterField } from '../../../form';
import { useModelFiltersContext } from '@rhino-project/core/hooks';
import { FilterTime } from '../../../Filter';
import {
  DateFormatter,
  parseAbsoluteToLocal,
  Time
} from '@internationalized/date';

const FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: true // Use 12-hour format with AM/PM
};

const buildDateTimePill = (
  attribute: RhinoAttribute,
  operator: string,
  newValue: string
) => {
  if (!newValue) return null;

  const locale = getLocale().replaceAll('_', '-');
  const formatter = new DateFormatter(locale, FORMAT_OPTIONS);

  const date = parseAbsoluteToLocal(newValue).toDate();

  return `${attribute.readableName} ${dateFilterOperatorLabel(
    attribute.format as string,
    operator
  )} ${formatter.format(date)}`;
};

export const ModelFilterTime = ({
  path,
  ...props
}: {
  path: string;
  [key: string]: unknown;
}) => {
  const { attribute, operator, operatorPath, ...rest } =
    useModelFilterField(path);

  const minValue = useMemo(() => {
    if (!attribute.minimum) return undefined;

    // FIXME how will this get returned from API?
    const date = parseAbsoluteToLocal(attribute.minimum as unknown as string);
    const time = new Time(date.hour, date.minute, date.second);

    // If the minimum is exclusive, we need to add a second to it so the value is not included
    if (attribute.exclusiveMinimum)
      return time.set({ second: date.second + 1 });

    return time;
  }, [attribute]);

  const maxValue = useMemo(() => {
    if (!attribute.maximum) return undefined;

    const date = parseAbsoluteToLocal(attribute.maximum as unknown as string);
    const time = new Time(date.hour, date.minute, date.second);

    // If the maximum is exclusive, we need to subtract a second to it so the value is not included
    if (attribute.exclusiveMaximum)
      return time.set({ second: date.second - 1 });

    return time;
  }, [attribute]);

  const watch = useWatch({ name: operatorPath });

  const { setPill } = useModelFiltersContext();

  useEffect(() => {
    if (watch !== undefined && watch !== '')
      setPill(operatorPath, buildDateTimePill(attribute, operator, watch));
  }, [attribute, operator, operatorPath, setPill, watch]);

  return (
    <FilterTime
      path={operatorPath}
      minValue={minValue}
      maxValue={maxValue}
      {...(rest as Record<string, unknown>)}
      {...props}
    />
  );
};
